import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './SalesMap.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Webpack renames Leaflet's bundled marker images, so point the default icon at the
// imported URLs explicitly (the classic CRA + Leaflet fix).
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

// Status-colored pins: the stock blue marker tinted via a CSS hue filter (see
// SalesMap.css) — Active = green, Pending = orange, Sold keeps the stock blue.
function tintedIcon(cls) {
  return L.icon({
    iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
    className: cls,
  });
}
const ICON_BY_STATUS = {
  active: tintedIcon('sm-pin-green'),
  pending: tintedIcon('sm-pin-orange'),
};
const iconFor = (status) => ICON_BY_STATUS[status] || new L.Icon.Default();

// Free Carto basemap (OSM data) — no API key. Attribution is required by both.
const TILE_URL = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

// Zoom/center the map to contain every pin (re-runs when the pin set changes).
// animate:false — a live filter changes the pin set on EVERY keystroke, and an
// in-flight zoom animation at unmount is exactly the Leaflet "_leaflet_pos"
// crash. An empty set leaves the map where it was.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lng], 14, { animate: false }); return; }
    map.fitBounds(L.latLngBounds(points.map(p => [p.lat, p.lng])), { padding: [30, 30], animate: false });
  }, [map, points]);
  return null;
}

// Generic pin map for property sales. `points`: [{ id, lat, lng, title, lines: [..] }].
// STAYS MOUNTED when a filter empties the pin set (an overlay note shows instead) —
// unmounting Leaflet mid-interaction is what crashed; callers gate mounting on the
// UNFILTERED dataset having any coordinates, not on the live-filtered set.
// zoomAnimation is off entirely: no zoom-transition callbacks can outlive the map.
function SalesMap({ points = [], height = 420 }) {
  const valid = useMemo(
    () => points.filter(p => Number.isFinite(p?.lat) && Number.isFinite(p?.lng)),
    [points],
  );
  // Remember the last real center so the container always has one even if it
  // mounts (or re-renders) while the current filter matches no mapped points.
  const lastCenter = useRef(null);
  if (valid.length) lastCenter.current = [valid[0].lat, valid[0].lng];
  const center = lastCenter.current || [47.61, -122.33];   // fallback: Seattle area

  return (
    <div className="sales-map-wrap" style={{ height }}>
      <MapContainer center={center} zoom={11} zoomAnimation={false} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
        <TileLayer url={TILE_URL} attribution={TILE_ATTR} />
        <FitBounds points={valid} />
        {valid.map(p => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={iconFor(p.status)}>
            <Popup>
              <div className="sales-map-pop">
                <strong>{p.title}</strong>
                {(p.lines || []).map((ln, i) => <div key={i}>{ln}</div>)}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {valid.length === 0 && (
        <div className="sales-map-empty">No matching properties have a map location.</div>
      )}
    </div>
  );
}

export default SalesMap;
